import { withTransaction } from '@elastic/apm-rum-react'
import { Campaign, HubCampaignsService, InfinityScroll, LogService, Notice } from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CampaignCardNotices from './components/campaign-card-notices/campaign-card-notices.component'
import './material-notices.page.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  campaign: Campaign
}

const MaterialNoticesPageComponent = ({ campaign }: Props) => {
  const [t] = useTranslation('campaignMaterialNoticesPage')
  const logService = useMemo(() => new LogService(), [])
  const hubCampaignsService = useMemo(() => new HubCampaignsService(), [])
  const [noticesItems, setNoticesItems] = useState<Array<Notice>>([])
  const [loadingNotices, setLoadingNotices] = useState(true)
  const [nextPage, setNextPage] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(15)

  logService.init('Campaign Material Notices Page')

  const handleLoadMore = useCallback(() => {
    setPage(page + 1)
    setLoadingNotices(true)

    if (!loadingNotices && nextPage) {
      try {
        hubCampaignsService.getNotices(campaign?._id, limit, page).then((response: any) => {
          setNoticesItems([...noticesItems, response?.data])
          const hasNextPage = response?.total > noticesItems?.length
          setNextPage(hasNextPage)
          setLoadingNotices(false)
        })
      } catch (error) {
        setLoadingNotices(false)
        logService.error({ message: 'Failed to get Notice Material Items', error })
      }
    }
  }, [noticesItems, loadingNotices, page, campaign, limit, logService, nextPage, hubCampaignsService, setPage, setLoadingNotices])

  const getNoticeMaterialItems = useCallback(() => {
    setPage(1)
    setLoadingNotices(true)
    try {
      hubCampaignsService.getNotices(campaign?._id, limit, page).then((response: any) => {
        setNoticesItems(response?.data)
        const hasNextPage = response?.total > response?.data?.length
        setNextPage(hasNextPage)
        setLoadingNotices(false)
      })
    } catch (error) {
      setLoadingNotices(false)
      logService.error({ message: 'Failed to get Notice Material Items', error })
    }
  }, [campaign, limit, page, hubCampaignsService, logService, setLoadingNotices, setNoticesItems])

  useEffect(() => {
    getNoticeMaterialItems()
  }, [getNoticeMaterialItems])

  if (loadingNotices) {
    return (
      <div className="display-flex justify-content-center">
        <SqLoader size="big" />
      </div>
    )
  }

  return (
    <section>
      {noticesItems?.length ? (
        <InfinityScroll
          className="campaign-notices-infinity-scroll"
          loading={loadingNotices}
          onLoadMore={handleLoadMore}
          hasMore={!!nextPage}
          endMessage={' '}
        >
          {noticesItems?.map((item) => (
            <CampaignCardNotices notice={item} key={item._id}></CampaignCardNotices>
          ))}
        </InfinityScroll>
      ) : (
        <div className="empty-schedule p-2">{t('emptyNotices')}</div>
      )}
    </section>
  )
}

export default withTransaction('MaterialNoticesPageComponent', 'component')(MaterialNoticesPageComponent)
